import React, { useState, useEffect } from 'react';
import {
    Modal,
    ModalOverlay,
    ModalContent,
    ModalHeader,
    ModalBody,
    ModalFooter,
    Button,
    Text,
    VStack,
    HStack,
    Switch,
    Box,
    Divider,
    useDisclosure,
    Collapse,
    Flex,
} from '@chakra-ui/react';
import { ChevronDownIcon, ChevronUpIcon } from '@chakra-ui/icons';

const CookieConsent = () => {
    const { isOpen, onOpen, onClose } = useDisclosure();
    const [showCustomize, setShowCustomize] = useState(false);
    const [cookiePreferences, setCookiePreferences] = useState({
        necessary: true,
        analytics: false,
        marketing: false,
        preferences: false,
    });

    useEffect(() => {
        const savedConsent = localStorage.getItem('cookieConsent');
        if (!savedConsent) {
            onOpen();
        } else {
            try {
                const parsed = JSON.parse(savedConsent);
                setCookiePreferences(parsed);
            } catch (error) {
                console.error('Error parsing cookie consent:', error);
            }
        }
    }, [onOpen]);

    const handleAcceptAll = () => {
        const allAccepted = {
            necessary: true,
            analytics: true,
            marketing: true,
            preferences: true,
        };
        setCookiePreferences(allAccepted);
        localStorage.setItem('cookieConsent', JSON.stringify(allAccepted));
        localStorage.setItem('cookieConsentDate', new Date().toISOString());
        onClose();
    };

    const handleRejectAll = () => {
        const onlyNecessary = {
            necessary: true,
            analytics: false,
            marketing: false,
            preferences: false,
        };
        setCookiePreferences(onlyNecessary);
        localStorage.setItem('cookieConsent', JSON.stringify(onlyNecessary));
        localStorage.setItem('cookieConsentDate', new Date().toISOString());
        onClose();
    };

    const handleSaveCustom = () => {
        localStorage.setItem('cookieConsent', JSON.stringify(cookiePreferences));
        localStorage.setItem('cookieConsentDate', new Date().toISOString());
        onClose();
    };

    const handleToggle = (category) => {
        if (category === 'necessary') return;
        setCookiePreferences((prev) => ({
            ...prev,
            [category]: !prev[category],
        }));
    };

    return (
        <Modal
            isOpen={isOpen}
            onClose={() => { }}
            closeOnOverlayClick={false}
            closeOnEsc={false}
            isCentered={false}
            size="md"
        >
            <ModalOverlay bg="blackAlpha.600" />
            <ModalContent
                position="fixed"
                bottom={{ base: "0", md: "20px" }}
                right={{ base: "0", md: "20px" }}
                left={{ base: "0", md: "auto" }}
                m={0}
                maxW={{ base: "100%", md: "420px" }}
                w={{ base: "100%", md: "auto" }}
                borderRadius={{ base: "0", md: "xl" }}
                borderTopRadius={{ base: "2xl", md: "xl" }}
                boxShadow="2xl"
                bg="white"
            >
                <ModalHeader pb={2}>
                    <Flex align="center" gap={2}>
                        <Text fontSize={{ base: "lg", md: "xl" }} fontWeight="bold" color="gray.800">
                            🍪 Cookie Settings
                        </Text>
                    </Flex>
                </ModalHeader>

                <ModalBody py={{ base: 2, md: 3 }}>
                    <VStack align="stretch" spacing={3}>
                        <Text fontSize={{ base: "xs", md: "sm" }} color="gray.600">
                            We use cookies to enhance your browsing experience, serve personalized
                            content, and analyze our traffic. By clicking "Accept All", you consent
                            to our use of cookies.
                        </Text>

                        <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => setShowCustomize(!showCustomize)}
                            rightIcon={showCustomize ? <ChevronUpIcon /> : <ChevronDownIcon />}
                            justifyContent="space-between"
                            w="full"
                            color="brand.600"
                            fontWeight="semibold"
                        >
                            Customize Settings
                        </Button>

                        <Collapse in={showCustomize} animateOpacity>
                            <VStack align="stretch" spacing={3} pt={2}>
                                <Divider />

                                <Box>
                                    <HStack justify="space-between" mb={1}>
                                        <Text fontWeight="semibold" fontSize="sm" color="gray.700">
                                            Necessary Cookies
                                        </Text>
                                        <Switch
                                            isChecked={cookiePreferences.necessary}
                                            isDisabled
                                            colorScheme="brand"
                                            size="sm"
                                        />
                                    </HStack>
                                    <Text fontSize="xs" color="gray.500">
                                        Essential for the website to function properly. Cannot be disabled.
                                    </Text>
                                </Box>

                                <Divider />

                                <Box>
                                    <HStack justify="space-between" mb={1}>
                                        <Text fontWeight="semibold" fontSize="sm" color="gray.700">
                                            Analytics Cookies
                                        </Text>
                                        <Switch
                                            isChecked={cookiePreferences.analytics}
                                            onChange={() => handleToggle('analytics')}
                                            colorScheme="brand"
                                            size="sm"
                                        />
                                    </HStack>
                                    <Text fontSize="xs" color="gray.500">
                                        Help us understand how visitors interact with our website.
                                    </Text>
                                </Box>

                                <Divider />

                                <Box>
                                    <HStack justify="space-between" mb={1}>
                                        <Text fontWeight="semibold" fontSize="sm" color="gray.700">
                                            Marketing Cookies
                                        </Text>
                                        <Switch
                                            isChecked={cookiePreferences.marketing}
                                            onChange={() => handleToggle('marketing')}
                                            colorScheme="brand"
                                            size="sm"
                                        />
                                    </HStack>
                                    <Text fontSize="xs" color="gray.500">
                                        Used to track visitors across websites for marketing purposes.
                                    </Text>
                                </Box>

                                <Divider />

                                <Box>
                                    <HStack justify="space-between" mb={1}>
                                        <Text fontWeight="semibold" fontSize="sm" color="gray.700">
                                            Preference Cookies
                                        </Text>
                                        <Switch
                                            isChecked={cookiePreferences.preferences}
                                            onChange={() => handleToggle('preferences')}
                                            colorScheme="brand"
                                            size="sm"
                                        />
                                    </HStack>
                                    <Text fontSize="xs" color="gray.500">
                                        Remember your preferences and settings for a better experience.
                                    </Text>
                                </Box>
                            </VStack>
                        </Collapse>
                    </VStack>
                </ModalBody>

                <ModalFooter pt={3} pb={{ base: 6, md: 4 }}>
                    <VStack w="full" spacing={2}>
                        {showCustomize ? (
                            <>
                                <Button
                                    colorScheme="brand"
                                    w="full"
                                    onClick={handleSaveCustom}
                                    size="sm"
                                >
                                    Save Preferences
                                </Button>
                                <HStack w="full" spacing={2}>
                                    <Button
                                        variant="outline"
                                        w="full"
                                        onClick={handleRejectAll}
                                        size="sm"
                                        colorScheme="gray"
                                    >
                                        Reject All
                                    </Button>
                                    <Button
                                        colorScheme="brand"
                                        variant="outline"
                                        w="full"
                                        onClick={handleAcceptAll}
                                        size="sm"
                                    >
                                        Accept All
                                    </Button>
                                </HStack>
                            </>
                        ) : (
                            <HStack w="full" spacing={2}>
                                <Button
                                    variant="outline"
                                    w="full"
                                    onClick={handleRejectAll}
                                    size="sm"
                                    colorScheme="gray"
                                >
                                    Reject All
                                </Button>
                                <Button
                                    colorScheme="brand"
                                    w="full"
                                    onClick={handleAcceptAll}
                                    size="sm"
                                >
                                    Accept All
                                </Button>
                            </HStack>
                        )}
                    </VStack>
                </ModalFooter>
            </ModalContent>
        </Modal>
    );
};

export default CookieConsent;